import axios from 'axios'
import store from '../index'
import router from '@/router/index'
import { make } from 'vuex-pathify'
import { USER_KEY } from '@/util/globals'
import { showError, showSuccess } from '@/util/helpers'
import Vuetify from '../../plugins/vuetify'
import { syncPushToken, releasePushToken } from '../../services/pushNotificationService'
import { getRefreshToken, endSession } from '@/plugins/refreshClient'

const buildPublicImageUrl = filePath => {
  if (!filePath || !/\/public\/imagens\//.test(String(filePath))) {
    return null
  }

  const bucket = process.env.VUE_APP_FIREBASE_STORAGE_BUCKET
  if (!bucket) {
    return null
  }

  return `https://storage.googleapis.com/${bucket}/${encodeURI(filePath)}`
}

const normalizeUserImage = user => {
  if (!user) {
    return user
  }

  const publicImgUrl = buildPublicImageUrl(user.img)
  if (!publicImgUrl) {
    return user
  }

  return {
    ...user,
    imgUrl: publicImgUrl,
  }
}

const normalizeCompanyLogo = company => {
  if (!company) {
    return company
  }

  const publicLogoUrl = buildPublicImageUrl(company.logoSmall)
  if (!publicLogoUrl) {
    return company
  }

  return {
    ...company,
    logoSmallUrl: publicLogoUrl,
  }
}

const state = {
  currentUser: null,
  userPermissions: [],
  currentCompany: null,

  loading: false,
}

const mutations = {
  setUser (state, user) {
    const normalizedUser = normalizeUserImage(user ? { ...user } : null)
    if (normalizedUser) {
      delete normalizedUser.refreshToken
    }
    localStorage.removeItem(USER_KEY)

    state.currentUser = normalizedUser
    if (normalizedUser) {
      axios.defaults.headers.common.Authorization = `Bearer ${normalizedUser.token}`
    } else {
      delete axios.defaults.headers.common.Authorization
      state.userPermissions = []
    }
  },

  updateUser (state, user) {
    const normalizedUser = normalizeUserImage(user ? { ...user } : null)
    if (normalizedUser) {
      delete normalizedUser.refreshToken
    }
    state.currentUser = normalizedUser
    localStorage.removeItem(USER_KEY)
  },

  setCompany (state, company) {
    const normalizedCompany = normalizeCompanyLogo(company)

    state.currentCompany = normalizedCompany
    if (normalizedCompany) {
      if (state.currentUser?.id) {
        localStorage.setItem(`campag:selected-company:${state.currentUser.id}`, String(normalizedCompany.id))
      }
      axios.defaults.headers.common.company = JSON.stringify({
        companyId: normalizedCompany.id,
        companySubId: normalizedCompany.companyId,
        movementType: normalizedCompany.movementType,
        profit: normalizedCompany.profit,
        costPercent: normalizedCompany.costPercent,
      })
    } else {
      delete axios.defaults.headers.common.company
    }
  },

  setUserPermissions (state, v) {
    state.userPermissions = v
  },
}

const actions = {
  login: ({ state }, payload) => {
    const user = payload?.credentials || payload
    const skipRedirect = !!payload?.skipRedirect
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/login`, user, {
      withCredentials: true,
      skipAuthRefresh: true,
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'X-Session-Mode': 'cookie' },
    })
      .then(res => {
        // Init app
        return store.dispatch('app/init', res.data).then(async initialized => {
          if (initialized) {
            syncPushToken({ receiveNotifications: Boolean(res.data.receiveNotifications) }).catch(error => console.warn('Push sync failed', error))
          }
          if (initialized && !skipRedirect) {
            const accessControl = store.get('app@accessControl')
            const isBlocked = await store.dispatch('app/isAccessBlocked', accessControl)

            if (isBlocked) {
              await router.push({ name: 'AcessoBloqueado' })
            } else {
              await router.push('/')
            }
            showSuccess('Usuário logado com sucesso!')
          }
          return res.data
        })
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  register: async ({ state }, auth) => {
    state.loading = true
    return axios
      .post(`${process.env.VUE_APP_API_URL}/register`, auth)
    // .then(res => {
    //     const { userId, companyId } = res.data

    //     state.auth.user.id = userId
    //     state.auth.company.id = companyId

    //     return res
    // })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  logout: async ({ commit, dispatch }) => {
    try {
      await releasePushToken()
    } catch (error) {
      console.warn('Push logout cleanup failed', error)
    }
    await dispatch('logoutApi')
    await store.dispatch('authStore/setUserConfig', { useMiniMenu: false, useDarkMode: false, colorSys: '#26A69A' })
    commit('setUser', null)
    commit('setCompany', null)
    store.commit('app/initialized', false)
    await router.push({ name: 'Login' })
    showSuccess('Sessão finalizada')
  },

  logoutApi: async () => endSession(),

  recoverPass: ({ state }, email) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/recoverPassword`, { email })
      .then(res => {
        showSuccess('Email de recuperação enviado com sucesso!')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  resetPass: ({ state }, user) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/resetPassword`, user)
      .then(res => {
        showSuccess('Senha restaurada com sucesso!')

        router.push({ name: 'Login' })
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  restoreSession: async ({ state, commit }) => {
    localStorage.removeItem(USER_KEY)
    state.loading = true
    try {
      const { data } = await getRefreshToken()
      await store.dispatch('app/init', data)
      syncPushToken({ receiveNotifications: Boolean(data.receiveNotifications) }).catch(error => console.warn('Push sync failed', error))
      return state.currentUser
    } catch (error) {
      commit('setUser', null)
      commit('setCompany', null)
      store.commit('app/initialized', false)
      if (error.response?.status === 401) {
        return null
      }
      throw error
    } finally {
      state.loading = false
    }
  },

  validateToken: async ({ dispatch }) => {
    const user = await dispatch('restoreSession')
    if (!user) {
      await router.push({ name: 'Login' })
    }
    return Boolean(user)
  },

  getUserPermissions: async ({ commit }, userId) => {
    return axios.get(`${process.env.VUE_APP_API_URL}/user-permissions/${userId}`)
      .then(res => {
        commit('setUserPermissions', res.data)
        return res.data
      })
      .catch(error => {
        commit('setUserPermissions', [])
        throw error
      })
      .finally(() => (state.loading = false))
  },

  setUserConfig: (context, user) => {
    store.commit('app/mini', user.useMiniMenu)
    Vuetify.framework.theme.themes[user.useDarkMode ? 'dark' : 'light'].primary = user.colorSys
    Vuetify.framework.theme.isDark = user.useDarkMode
  },
}

const getters = make.getters(state)

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
